import './CompareRunsPanel.css';
import { useState, useEffect, useMemo } from 'react';
import { Bug, X } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import { visibleCompareItems, isSelectable, countOverwrites } from '../../pages/quality/compareRuns';

const JIRA_BASE = 'https://contentsquare.atlassian.net/browse';

function StatusPill({ status }) {
  const value = status || 'unexecuted';
  return <span className={`cr-status cr-status-${value}`}>{value}</span>;
}

function Defects({ defects }) {
  if (!defects?.length) return <span className="cr-muted">—</span>;
  return defects.map(d => (
    <a key={d} className="te-defect-tag" href={`${JIRA_BASE}/${d}`} target="_blank" rel="noreferrer">
      <Bug size={11} /> {d}
    </a>
  ));
}

// Compares `sourceId` (e.g. rc1) against the target run and copies selected results into the target.
function CompareRunsPanel({ targetRunId, targetName, runs, sourceId, updatedBy, onSourceChange, onClose, onCopied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOverwrite, setConfirmOverwrite] = useState(null);
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const sourceOptions = runs.filter(r => r.id !== targetRunId);

  useEffect(() => { setResult(null); }, [sourceId, targetRunId]);

  useEffect(() => {
    setSelected(new Set());
    if (!sourceId) { setData(null); return; }
    setLoading(true);
    setError(null);
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/compare`, { params: { source: sourceId, target: targetRunId } })
      .then(res => setData(res.data))
      .catch(err => { setData(null); setError(err?.response?.data?.message || 'Failed to compare runs.'); })
      .finally(() => setLoading(false));
  }, [sourceId, targetRunId, reloadKey]);

  const visible = useMemo(() => visibleCompareItems(data?.items ?? [], showAll), [data, showAll]);
  const selectableVisible = visible.filter(isSelectable);
  const allSelected = selectableVisible.length > 0 && selectableVisible.every(i => selected.has(i.testCaseKey));
  const someSelected = selectableVisible.some(i => selected.has(i.testCaseKey));

  function toggle(key) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function toggleAll() {
    setSelected(prev => {
      const next = new Set(prev);
      selectableVisible.forEach(i => (allSelected ? next.delete(i.testCaseKey) : next.add(i.testCaseKey)));
      return next;
    });
  }

  function requestCopy() {
    const overwrites = countOverwrites(data.items, selected);
    if (overwrites > 0) setConfirmOverwrite(overwrites);
    else doCopy();
  }

  function doCopy() {
    setConfirmOverwrite(null);
    setCopying(true);
    api.post(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/copy-results`, {
      sourceRunId: sourceId,
      targetRunId,
      testCaseKeys: [...selected],
      updatedBy,
    })
      .then(res => {
        setResult(res.data);
        onCopied();
        setReloadKey(k => k + 1);
      })
      .catch(err => setError(err?.response?.data?.message || 'Failed to copy results.'))
      .finally(() => setCopying(false));
  }

  return (
    <div className="cr-panel">
      <div className="cr-header">
        <h2 className="cr-title">Carry over results to <strong>{targetName}</strong></h2>
        <button className="cr-close" onClick={onClose} title="Close compare"><X size={16} /></button>
      </div>

      <div className="cr-toolbar">
        <label className="cr-source-label">
          Copy from
          <select className="cr-source-select" value={sourceId || ''} onChange={e => onSourceChange(e.target.value)}>
            <option value="">Select a run…</option>
            {sourceOptions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        {data && (
          <>
            <span className="cr-chip">{data.summary.differs} differ</span>
            <span className="cr-chip">{data.summary.onlyInSource} only in {data.source.name}</span>
            <span className="cr-chip">{data.summary.onlyInTarget} only in {data.target.name}</span>
            <label className="cr-show-all">
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
              Show tests with matching status
            </label>
            <button className="cr-copy-btn" disabled={selected.size === 0 || copying} onClick={requestCopy}>
              {copying ? 'Copying…' : `Copy ${selected.size} result${selected.size === 1 ? '' : 's'} to ${data.target.name}`}
            </button>
          </>
        )}
      </div>

      {!sourceId && <p className="tm-state-msg">Select the run to copy results from.</p>}
      {loading && <p className="tm-state-msg">Comparing…</p>}
      {error && <p className="tm-state-msg tm-error">{error}</p>}
      {result && (
        <p className="cr-result">
          Copied {result.copied.length}
          {result.skipped.length > 0 && `, skipped ${result.skipped.length}`}
          {result.notInTarget.length > 0 && `, ${result.notInTarget.length} not in target`}.
        </p>
      )}

      {data && !loading && (
        <div className="cr-table-scroll">
        <table className="tm-table cr-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                  onChange={toggleAll}
                  disabled={selectableVisible.length === 0}
                />
              </th>
              <th>Key</th>
              <th>Title</th>
              <th>{data.source.name}</th>
              <th>{data.target.name}</th>
              <th>Defects ({data.source.name})</th>
              <th>Defects ({data.target.name})</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={7} className="cr-muted">Nothing to carry over — all statuses match in both runs.</td></tr>
            )}
            {visible.map(item => {
              const selectable = isSelectable(item);
              return (
                <tr key={item.testCaseKey} className={selectable ? '' : 'cr-row-disabled'}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.testCaseKey}`}
                      checked={selected.has(item.testCaseKey)}
                      disabled={!selectable}
                      onChange={() => toggle(item.testCaseKey)}
                    />
                  </td>
                  <td>{item.testCaseKey}</td>
                  <td>{item.title || '—'}</td>
                  <td>{item.inSource ? <StatusPill status={item.sourceStatus} /> : <span className="cr-muted">not in run</span>}</td>
                  <td>{item.inTarget ? <StatusPill status={item.targetStatus} /> : <span className="cr-muted">not in run</span>}</td>
                  <td><Defects defects={item.sourceDefects} /></td>
                  <td><Defects defects={item.targetDefects} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {confirmOverwrite != null && (
        <div className="te-modal-overlay" onClick={() => setConfirmOverwrite(null)}>
          <div className="te-modal" onClick={e => e.stopPropagation()}>
            <h3 className="te-modal-title">Overwrite existing results?</h3>
            <p className="te-modal-sub">
              {confirmOverwrite} of the selected tests already have a result in {data.target.name}. They will be overwritten.
            </p>
            <div className="te-modal-actions">
              <button className="te-modal-cancel" onClick={() => setConfirmOverwrite(null)}>Cancel</button>
              <button className="te-modal-save" onClick={doCopy}>Overwrite and copy</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompareRunsPanel;
